import { useDisclosure, Box, IconButton } from '@chakra-ui/react';
import MuiIcon from 'components/MuiIcon';
import React, { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import useGetColors from 'shared/hooks/useGetColors';
import { getCookies, removeCookies } from 'shared/utils/devUtils';
import { SearchBar } from '../horizon-ui/navbar/searchBar/SearchBar';
import Pagination from '../Pagination';
import FiltersDrawer from './FiltersDrawer';
import { TableNameE } from 'shared/api/types/common';
import { ListQueryRequestType } from 'shared/api/types/typedQueries';
import { FilterField } from 'shared/types/filters';
import { ColumnDef } from '@tanstack/react-table';
import type { DefaultTableData } from './DraggableRow';
import useGetSortingMethods from './hooks/useGetSortingMethods';

interface Props<QueryData extends ListQueryRequestType<DefaultTableData>> extends ReturnType<typeof useGetSortingMethods<QueryData>> {
  children: React.ReactNode;
  columns: ColumnDef<ArrayElement<QueryData['response']['data']>>[];
  setColumnsToRender: (columns: ColumnDef<ArrayElement<QueryData['response']['data']>>[]) => void;
  tableName: TableNameE;
  filterFields?: FilterField[];
  hidePagination?: boolean;
  disableSearch?: boolean;
}

const TableContainer = <QueryData extends ListQueryRequestType<DefaultTableData>>({
  children,
  columns,
  setColumnsToRender,
  tableName,
  filterFields,
  hidePagination,
  filters,
  page,
  search,
  disableSearch,
  size,
  total,
  changePage,
  changeSearch,
  changeSize,
  clearFilter,
  handleFilter,
}: Props<QueryData>) => {
  type TData = ArrayElement<QueryData['response']['data']>;
  const { red500, blue500 } = useGetColors();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const methods = useForm({ defaultValues: filters as UnknownRecord });


  useEffect(() => {
    const columnsFromCookies = getCookies(`columns/${tableName}`);
    if (!columnsFromCookies) {
      setColumnsToRender(columns)
      return;
    }
    const newColumns = columns.filter((column) => columnsFromCookies[column.id])
    setColumnsToRender(newColumns);
  }, [columns])

  const prepareColumnDefaultValues = () => {
    const columnsFromCookies = getCookies(`columns/${tableName}`);
    return (
      columns.reduce((prev, curr) => ({
        ...prev,
        [curr.id]: columnsFromCookies?.[curr.id] ?? true,
      }), {})
    )
  };

  const getColumnDefaultValues = () => (
    columns.reduce((prev, curr) => ({
      ...prev,
      [curr.id]: true,
    }), {})
  );

  const showColumnsMethods = useForm({ defaultValues: prepareColumnDefaultValues() as UnknownRecord });

  const handleResetColumnsToRender = () => {
    showColumnsMethods.reset(getColumnDefaultValues());
    handleSortColumns(getColumnDefaultValues() as Record<keyof TData, boolean>);
    removeCookies(`columns/${tableName}`);
  }

  const handleResetFilters = () => {
    clearFilter();
    methods.reset();
    handleResetColumnsToRender();
  };

  const handleSortColumns = (newColumns: Record<keyof TData, boolean>) => {
    setColumnsToRender(columns.filter((column) => newColumns[column.id]))
  }


  return (
    <div>
      <Box overflowX="auto" mt={{ sm: 2, md: 25 }}>
        <Box
          display="flex" gap={{ sm: 3, md: 6 }}
          position={{ sm: 'unset', md: 'absolute' }} top={5} right={5}
        >
          {!!filterFields && (
            <>
              {!disableSearch && <SearchBar searchValue={search} handleChangeSearch={changeSearch} />}
              <IconButton aria-label="Filters" onClick={onOpen} borderRadius="10px" backgroundColor="secondaryGray.300" icon={<MuiIcon name="filter_list" color={blue500} />} />
              <IconButton
                aria-label="Reset filters"
                onClick={handleResetFilters}
                borderRadius="10px"
                backgroundColor="secondaryGray.300"
                icon={<MuiIcon name="filter_list_off" color={red500} />}
              />
            </>
          )}
        </Box>
        {children}
      </Box>
      {!hidePagination && (
        <Pagination
          setSize={changeSize}
          setPage={changePage}
          page={page}
          size={size}
          total={total}
        />
      )}
      <FiltersDrawer
        isOpen={isOpen}
        onClose={onClose}
        handleFilter={handleFilter}
        handleSortColumns={handleSortColumns as FixMeLater}
        columns={columns as FixMeLater}
        values={filters}
        fields={filterFields ?? []}
        methods={methods}
        showColumnsMethods={showColumnsMethods}
        tableName={tableName}
      />
    </div>
  );
}

export default TableContainer;
